import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import firebase from '@/shared/services/firebase';

const SCRIPT_SRC_BASE = 'https://app.termly.io';

/**
 * Termly Consent Management Platform (CMP) component.
 * Loads the Termly consent banner script which provides:
 * - Cookie consent banner on first visit
 * - Cookie scanning and categorization
 *
 * autoBlock is disabled because it blocks cross-origin images (org logos)
 * until consent, breaking the directory card view. Instead, analytics are
 * gated manually: Firebase Analytics is only initialized after the user
 * accepts cookies via the Termly consent event.
 *
 * When adding new analytics/tracking scripts, do NOT load them at page load.
 * Instead, initialize them inside the consent listener in this component.
 */
export default function TermlyCMP({ masterConsentsOrigin, websiteUUID }) {
    const scriptSrc = useMemo(() => {
        const src = new URL(SCRIPT_SRC_BASE);
        src.pathname = `/resource-blocker/${websiteUUID}`;

        if (masterConsentsOrigin) {
            src.searchParams.set('masterConsentsOrigin', masterConsentsOrigin);
        }

        return src.toString();
    }, [masterConsentsOrigin, websiteUUID]);

    const isScriptAdded = useRef(false);

    useEffect(() => {
        if (isScriptAdded.current) return;

        const script = document.createElement('script');
        script.src = scriptSrc;
        script.async = true;
        document.head.appendChild(script);

        isScriptAdded.current = true;

        return () => {
            const existingScript = document.querySelector(
                `script[src="${scriptSrc}"]`
            );
            if (existingScript) {
                existingScript.remove();
            }
        };
    }, [scriptSrc]);

    // Listen for Termly consent events to gate analytics initialization
    useEffect(() => {
        function handleConsent() {
            // Termly stores consent in a cookie; check if analytics accepted
            const consentState = window.Termly?.getConsentState?.();
            if (consentState?.analytics) {
                firebase.initAnalytics();
            }
        }

        // Check on mount in case consent was previously given
        handleConsent();

        // Termly dispatches this custom event when consent changes
        window.addEventListener('termly:consent', handleConsent);
        return () =>
            window.removeEventListener('termly:consent', handleConsent);
    }, []);

    const location = useLocation();

    useEffect(() => {
        if (window.Termly && typeof window.Termly.initialize === 'function') {
            window.Termly.initialize();
        }
    }, [location.pathname, location.search]);

    return null;
}

TermlyCMP.propTypes = {
    masterConsentsOrigin: PropTypes.string,
    websiteUUID: PropTypes.string.isRequired,
};
